import React, {useEffect, useMemo, useRef, useState} from 'react';

import {yupResolver} from '@hookform/resolvers/yup';
import axios, {CancelTokenSource} from 'axios';
import {useStore} from 'effector-react';
import {Controller, useForm} from 'react-hook-form';
import {Trans, useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import * as yup from 'yup';

import {purchaseMarkingCodes} from 'api/billing';
import {Button, ButtonVariant} from 'components/ui-kit/Button';
import {Checkbox} from 'components/ui-kit/Checkbox';
import {Input} from 'components/ui-kit/Input';
import {LoaderDoted} from 'components/ui-kit/LoaderDoted';
import {Modal} from 'components/ui-kit/Modal';
import {BILLING_OFFER_URL} from 'core/constants/env';

import {profile$} from 'models/auth';
import {billingAccess$} from 'models/profile/billing/access';

import {
    ButtonsContainer,
    CheckboxText,
    ErrorMessage,
    FieldContainer,
    FieldsContainer,
    Form,
    ModalContainer,
    ModalContainerCentered,
    ModalContainerSuccess,
    OfferLink,
    TitleSuccess,
} from './styled';

interface InvoiceGeneratingModalProps {
    show: boolean;
    onClose: () => void;
}

type ModalState = 'form' | 'loading' | 'success';

interface FormData {
    quantity: string;
    agreed: boolean;
}

export const InvoiceGeneratingModal: React.FC<InvoiceGeneratingModalProps> = ({show, onClose}) => {
    const {t} = useTranslation();
    const [state, setState] = useState<ModalState>('form');
    const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
    const [filename, setFilename] = useState<string | null>(null);
    const profile = useStore(profile$);
    const access = useStore(billingAccess$);
    const request = useRef<CancelTokenSource | null>(null);

    const setTitle = useMemo(() => {
        switch (state) {
            case 'form':
                return t('pages.profile.billing.invoiceGenerating.titleForm');
            case 'loading':
                return t('pages.profile.billing.invoiceGenerating.titleLoading');
            case 'success':
                return t('pages.profile.billing.invoiceGenerating.titleSuccess');
        }
    }, [t, state]);
    const validationSchema = useMemo(
        () =>
            yup.object().shape({
                quantity: yup
                    .string()
                    .required(t('components.form.required'))
                    .test('is-positive', t('positiveIntegerQuantity'), (value) => {
                        if (!value) return false;
                        const numValue = Number(value);
                        return (
                            /^\d+$/.test(value) &&
                            Number.isSafeInteger(numValue) &&
                            numValue > 0 &&
                            numValue <= 2147483647
                        );
                    }),
                agreed: yup
                    .boolean()
                    .required(t('components.form.required'))
                    .oneOf(
                        [true],
                        t('pages.profile.billing.invoiceGenerating.messages.shouldBeAgreed'),
                    ),
            }),
        [t],
    );

    const {
        control,
        handleSubmit,
        formState: {errors, isValid},
        reset,
    } = useForm<FormData>({
        mode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: {
            quantity: '',
            agreed: false,
        },
    });

    useEffect(
        () => () => {
            request.current?.cancel();
            request.current = null;
        },
        [],
    );
    useEffect(() => {
        request.current?.cancel();
        request.current = null;
        setState('form');
        setPdfBlob(null);
        setFilename(null);
        reset();
        onClose();
    }, [profile, access.canRequestInvoice]);

    const onSubmit = async (data: FormData) => {
        if (!show || !access.canRequestInvoice || request.current || state !== 'form') return;
        const source = axios.CancelToken.source();
        request.current = source;
        setState('loading');

        try {
            const {blob, filename} = await purchaseMarkingCodes(
                {
                    quantity: Number(data.quantity),
                    email: profile?.email ?? '',
                },
                source.token,
            );
            source.token.throwIfRequested();

            setPdfBlob(blob);
            setFilename(filename);
            setState('success');
        } catch (error) {
            if (!source.token.reason) {
                setState('form');
                toast.error(t('pages.profile.billing.deposit.messages.error'));
            }
        } finally {
            if (request.current === source) request.current = null;
        }
    };

    const handleDownload = () => {
        if (!show || !access.canRequestInvoice || !pdfBlob) return;

        const url = URL.createObjectURL(pdfBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename ?? `invoice-${Date.now()}.pdf`;
        document.body.appendChild(a);
        a.click();
        URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setTimeout(() => {
            handleClose();
        }, 1000);
    };

    const handleClose = () => {
        if (state === 'loading') return;

        setState('form');
        reset();
        setPdfBlob(null);
        setFilename(null);
        onClose();
    };

    const renderForm = () => (
        <ModalContainer>
            <Form onSubmit={handleSubmit(onSubmit)}>
                <FieldsContainer>
                    <FieldContainer>
                        <Controller
                            control={control}
                            name="quantity"
                            render={(props) => (
                                <Input
                                    {...props}
                                    placeholder={t(
                                        'pages.profile.billing.invoiceGenerating.quantityPlaceholder',
                                    )}
                                    type="number"
                                    hasError={!!errors.quantity}
                                    label={t(
                                        'pages.profile.billing.invoiceGenerating.quantityLabel',
                                    )}
                                />
                            )}
                        />
                        {errors.quantity && <ErrorMessage>{errors.quantity.message}</ErrorMessage>}
                    </FieldContainer>
                    <FieldContainer>
                        <Controller
                            control={control}
                            name="agreed"
                            render={(props) => (
                                <Checkbox
                                    checked={props.value}
                                    onChange={(e) => props.onChange(e.target.checked)}
                                >
                                    <CheckboxText>
                                        <Trans
                                            i18nKey="pages.profile.billing.invoiceGenerating.agreementLabel"
                                            components={{
                                                offer: BILLING_OFFER_URL ? (
                                                    <OfferLink
                                                        href={BILLING_OFFER_URL}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(event) => event.stopPropagation()}
                                                    />
                                                ) : (
                                                    <span />
                                                ),
                                            }}
                                        />
                                    </CheckboxText>
                                </Checkbox>
                            )}
                        />
                        {errors.agreed && <ErrorMessage>{errors.agreed.message}</ErrorMessage>}
                    </FieldContainer>
                </FieldsContainer>
                <ButtonsContainer>
                    <Button variant={ButtonVariant.Outline} type="button" onClick={handleClose}>
                        {t('pages.profile.billing.invoiceGenerating.buttons.cancel')}
                    </Button>
                    <Button
                        variant={ButtonVariant.Normal}
                        type="submit"
                        disabled={!isValid || !access.canRequestInvoice || state !== 'form'}
                    >
                        {t('pages.profile.billing.invoiceGenerating.buttons.buy')}
                    </Button>
                </ButtonsContainer>
            </Form>
        </ModalContainer>
    );

    const renderSuccess = () => (
        <ModalContainerSuccess>
            <TitleSuccess>{t('pages.profile.billing.invoiceGenerating.successTitle')}</TitleSuccess>
            <Button variant={ButtonVariant.Normal} type="button" onClick={handleDownload}>
                {t('pages.profile.billing.invoiceGenerating.buttons.download')}
            </Button>
        </ModalContainerSuccess>
    );

    return (
        <Modal
            title={setTitle}
            show={show && access.canRequestInvoice}
            modalWidth={0}
            onClose={handleClose}
            hideCloseButton={state !== 'form'}
            closable={state === 'form'}
        >
            {state === 'form' && renderForm()}
            {state === 'loading' && (
                <ModalContainerCentered role="status" aria-live="polite">
                    <LoaderDoted />
                </ModalContainerCentered>
            )}
            {state === 'success' && renderSuccess()}
        </Modal>
    );
};
